<template>
  <div class="container">
    <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="errorMessage = ''" aria-label="Close"></button>
    </div>

    <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" @click="successMessage = ''" aria-label="Close"></button>
    </div>

    <form @submit.prevent="submitForm">
    <div v-for="fieldKey in Object.keys(fields).filter(k => !k.endsWith('_currency') && k !== 'is_system')" :key="fieldKey" class="mb-3">
      <label class="form-label d-block">{{ fieldKey }}</label>

      <InputRadioGroup v-if="fieldKey==='transaction_type'" v-model="fields[fieldKey]" :options="transactionTypes" :name="fieldKey" />

      <select v-else-if="fieldKey==='feeType'" v-model="fields[fieldKey]" class="form-select">
        <option v-for="option in feeTypes" :key="option" :value="option">{{option}}</option>
      </select>

      <InputRadioGroup v-else-if="fieldKey==='payments_entity'" v-model="fields[fieldKey]" :options="paymentEntities" :name="fieldKey" />

      <utc-date-input v-else-if="fieldKey==='transaction_date'" v-model="fields[fieldKey]" />

      <select v-else-if="fieldKey==='transaction_status'" v-model="fields[fieldKey]" class="form-select">
        <option v-for="option in transactionStatuses" :key="option" :value="option">{{option}}</option>
      </select>

      <select v-else-if="fieldKey==='booking_entry'" v-model="fields[fieldKey]" class="form-select">
        <option v-for="option in ['DEBIT','CREDIT']" :key="option" :value="option">{{option}}</option>
      </select>

      <money-input v-else-if="fieldKey.endsWith('_amount')" v-model="fields[fieldKey]" />

      <input v-else type="text" v-model="fields[fieldKey]" class="form-control">

    </div>
<!--    <pre>{{ transaction }}</pre>-->
<!--    <pre>{{ fields }}</pre>-->
      <div class="d-grid gap-3">
        <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
          <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <i v-else class="bi bi-floppy me-2"></i>
          {{ isSubmitting ? 'Saving...' : (transaction.transactionId ? 'Save Changes' : 'Add Transaction') }}
        </button>
      </div>
    </form>
  </div>
</template>
<script setup>
import InputRadioGroup from "@/app/components/Utils/InputRadioGroup.vue"
import UtcDateInput from "@/app/components/Utils/UtcDateInput.vue"
import MoneyInput from "@/app/components/Utils/MoneyInput.vue"
import {reactive, defineProps, defineEmits, ref} from "vue"
import { useStore } from "vuex";
const store = useStore()
const emit = defineEmits(['saved'])
const props = defineProps({
  transaction: {
    type: Object,
    required: false,
    default: () => ({}),
  }
})

const errorMessage = ref('')
const successMessage = ref('')
const isSubmitting = ref(false)

// Generate unique transaction ID for manual entries
const generateTransactionId = () => {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 9)
  return `MANUAL-${timestamp}-${random}`
}

const fields = reactive({
  transaction_id: props.transaction.transactionId || generateTransactionId(),
  transaction_date: props.transaction.transactionDate || new Date().toISOString(),
  transaction_type: props.transaction.transactionType || '',
  transaction_amount: props.transaction.amount?.value || '0.00',
  booking_entry: props.transaction.bookingEntry || 'DEBIT',
  transaction_currency: props.transaction.amount?.currency || 'USD',
  order_id: props.transaction.orderId || '',
  transaction_memo: props.transaction.transactionMemo || '',
  sales_record_reference: props.transaction.salesRecordReference || '0',
  buyer: props.transaction.buyer?.username || '',
  total_fee_basis_amount: props.transaction.totalFeeBasisAmount?.value || '0.00',
  total_fee_basis_currency: props.transaction.totalFeeBasisAmount?.currency || 'USD',
  total_fee_amount: props.transaction.totalFeeAmount?.value || '0.00',
  total_fee_currency: props.transaction.totalFeeAmoount?.currency || 'USD',
  ebay_collected_tax_amount: props.transaction.ebayCollectedTaxAmount?.value || '0.00',
  ebay_collected_tax_currency: props.transaction.ebayCollectedTaxAmount?.currency || 'USD',
  transaction_status: props.transaction.transactionStatus || 'Completed',
  feeType: props.transaction.feeType || '',
  payments_entity: props.transaction.paymentsEntity || '',
  is_system: props.transaction.isSystem ?? 0,  // Default to 0 for manual entries
})
const feeTypes = ["" ,"AD_FEE", "INSERTION_FEE", "OTHER_FEES"]
const transactionTypes = [
  {value: "SHIPPING_LABEL", label: 'Shipping'},
  {value: "REFUND", label: 'Refund'},
  {value: "NON_SALE_CHARGE", label: 'Non'},
  {value: "SALE", label: 'Sale'},
  {value: "CREDIT", label: 'Credit'},
  {value: "DISPUTE", label: 'Dispute'},
  {value: "COGS", label: 'COGS'},
]
const paymentEntities = [
  {value:'', label:'-'},
  {value:'eBay Commerce Inc.', label:'eBay'},
  {value:'USPS', label:'USPS'},
  {value:'POSHMARK', label:'Poshmark'},
  {value:'MERCARI', label:'Mercari'},
  {value:'DEPOP', label:'Depop'},
]
const transactionStatuses = [
  "FUNDS_AVAILABLE_FOR_PAYOUT",
  "FUNDS_PROCESSING",
  "PAYOUT",
  "Completed",
]

async function submitForm() {
  errorMessage.value = ''
  successMessage.value = ''
  isSubmitting.value = true

  console.log('Submitting transaction:', fields)

  try {
    const result = await store.dispatch('eshop/upsertTransaction', { transaction: fields })
    console.log('Transaction upsert result:', result)

    if (result && result.error) {
      errorMessage.value = result.error
      console.error('Transaction error:', result.error)
    } else {
      successMessage.value = 'Transaction saved successfully!'
      console.log('Transaction saved successfully, insertId:', result)
      emit('saved', result)
    }
  } catch (error) {
    errorMessage.value = error.message || error.toString() || 'An error occurred while saving the transaction'
    console.error('Transaction submission error:', error)
  } finally {
    isSubmitting.value = false
  }
}


</script>
